import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Input, Button, Tooltip, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { PlusCircleOutlined, FormOutlined } from '@ant-design/icons';
import api from '@services/api';
import CustomTag from '@components/CustomTag';

type ModalTemplateType = {
  defaultValues?: {
    id: string;
    name: string;
    text: string;
    active: boolean;
  };
  mutate: () => void;
};

const ModalTemplate = ({ defaultValues, mutate }: ModalTemplateType) => {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const { t: translate } = useTranslation('sms-send');

  const onClose = () => {
    setVisible(false);
    form.resetFields();
  };

  useEffect(() => {
    if (defaultValues) {
      form.setFieldsValue(defaultValues);
    }
  }, [defaultValues, form]);

  const handleOk = () => {
    form.validateFields().then(async values => {
      try {
        if (defaultValues) {
          await api.put(
            `/service-proxy/sms-templates/${defaultValues.id}`,
            values,
          );
          message.success(translate('template.updated'));
        } else {
          await api.post('/service-proxy/sms-templates', values);
          message.success(translate('template.created'));
        }
        onClose();
      } catch (err) {
        onClose();
        message.error(translate('template.error'));
      }
      mutate();
    });
  };

  const textAreaRef = useRef<any>(null);

  const handleAddVariable = () => {
    const { textArea } = textAreaRef.current.resizableTextArea;
    const currentValue = textArea.value;
    const { selectionStart } = textArea;
    const newValue = `${currentValue.substring(
      0,
      selectionStart,
    )} {{}}${currentValue.substring(selectionStart)}`;
    textArea.value = newValue;
    textArea.selectionStart = selectionStart + 3;
    textArea.selectionEnd = selectionStart + 3;
    textArea.focus();
  };

  return (
    <>
      <Tooltip
        title={
          defaultValues
            ? translate('template.edit')
            : translate('template.create')
        }
      >
        <Button
          onClick={() => setVisible(true)}
          icon={defaultValues ? <FormOutlined /> : <PlusCircleOutlined />}
        />
      </Tooltip>

      <Modal
        open={visible}
        onCancel={onClose}
        onOk={handleOk}
        title={
          defaultValues
            ? translate('template.edit')
            : translate('template.create')
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={translate('table.name')}
            rules={[
              { required: true, message: translate('template.name.required') },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="text"
            label={
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  gridGap: 8,
                  alignItems: 'center',
                }}
              >
                <p style={{ margin: '0px' }}>{translate('table.text')}</p>
                <Button onClick={handleAddVariable}>
                  {translate('template.newVariable')}
                </Button>
              </div>
            }
            rules={[
              { required: true, message: translate('template.text.required') },
            ]}
          >
            <Input.TextArea ref={textAreaRef} style={{ marginTop: '10px' }} />
          </Form.Item>
          <div>
            <p>{translate('template.example')}</p>
            <div style={{ marginTop: '10px' }}>
              <CustomTag color="blue">{`{{nome}}`}</CustomTag>
              <CustomTag color="blue">{`{{idade}}`}</CustomTag>
              <CustomTag color="blue">{`{{cidade}}`}</CustomTag>
            </div>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ModalTemplate;
